import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError } from "../lib/api";
import { nextDay, onCheckinChange } from "../lib/dates";
import { useI18n } from "../context/I18nContext";

export default function BookingModal({ open, onClose, apartment, roomType, initial }) {
  const { t } = useI18n();
  const [step, setStep] = useState("quote"); // quote | pay | done
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    checkin: initial?.checkin || "",
    checkout: initial?.checkout || "",
    adults: initial?.adults || 2,
    children: initial?.children || 0,
    pets: initial?.pets || false,
    guest_name: initial?.name || "",
    guest_email: "",
    guest_phone: initial?.phone || "",
  });
  const dType = initial?.discount_type || "percent";
  const dValue = initial?.discount_value || 0;
  const [order, setOrder] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [config, setConfig] = useState({ paypal_configured: false, paypal_client_id: "" });
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [codeMsg, setCodeMsg] = useState("");

  useEffect(() => {
    if (open) {
      setStep("quote");
      setOrder(null);
      setAcceptTerms(false);
      setCodeInput(""); setAppliedCode(""); setCodeMsg("");
      setForm((f) => ({ ...f, checkin: initial?.checkin || f.checkin, checkout: initial?.checkout || f.checkout, adults: initial?.adults || f.adults, children: initial?.children ?? f.children, pets: initial?.pets ?? f.pets, guest_name: initial?.name || f.guest_name, guest_phone: initial?.phone || f.guest_phone }));
      api.get("/public/config").then((r) => setConfig(r.data)).catch(() => {});
    }
  }, [open, initial]);

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    try {
      const { data } = await api.post("/discount-codes/validate", { code: codeInput.trim(), apartment_id: apartment.id });
      if (data.valid) { setAppliedCode(data.code); setCodeMsg(`✓ ${data.message}`); }
      else { setAppliedCode(""); setCodeMsg(data.message || "Codice non valido"); }
    } catch (e) { setAppliedCode(""); setCodeMsg("Codice non valido"); }
  };

  useEffect(() => {
    const check = async () => {
      if (!open || !form.checkin || !form.checkout) return;
      setLoading(true);
      try {
        const { data } = await api.post("/availability", {
          apartment_id: apartment?.id,
          room_type_id: roomType?.id || null,
          checkin: form.checkin,
          checkout: form.checkout,
          adults: form.adults,
          children: form.children,
          pets: form.pets,
          discount_type: dType,
          discount_value: dValue,
          discount_code: appliedCode || null,
        });
        setQuote(data);
      } catch (e) {
        setQuote(null);
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [open, form.checkin, form.checkout, form.adults, form.children, form.pets, apartment, roomType, appliedCode]);

  const startPayment = async () => {
    if (!form.guest_name || !form.guest_email) {
      toast.error("Inserisci nome ed email");
      return;
    }
    if (!acceptTerms) {
      toast.error("Devi accettare le condizioni di prenotazione");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/create-order", {
        apartment_id: apartment.id,
        room_type_id: roomType?.id || null,
        checkin: form.checkin,
        checkout: form.checkout,
        adults: form.adults,
        children: form.children,
        pets: form.pets,
        discount_type: dType,
        discount_value: dValue,
        discount_code: appliedCode || null,
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        accept_terms: true,
      });
      setOrder(data);
      if (data.simulated) {
        // PayPal not configured yet: complete via simulated capture
        await capture(data.order_id, data.booking_id);
      } else {
        setStep("pay");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const startStripePayment = async () => {
    if (!form.guest_name || !form.guest_email) {
      toast.error("Inserisci nome ed email");
      return;
    }
    if (!acceptTerms) {
      toast.error("Devi accettare le condizioni di prenotazione");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/stripe/create-checkout", {
        apartment_id: apartment.id,
        room_type_id: roomType?.id || null,
        checkin: form.checkin,
        checkout: form.checkout,
        adults: form.adults,
        children: form.children,
        pets: form.pets,
        discount_type: dType,
        discount_value: dValue,
        discount_code: appliedCode || null,
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        accept_terms: true,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe hosted checkout
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const capture = async (orderId, bookingId) => {
    setLoading(true);
    try {
      await api.post("/bookings/capture-order", { order_id: orderId, booking_id: bookingId });
      setStep("done");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-fir-deep/70 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="booking-modal"
      >
        <motion.div
          data-lenis-prevent
          className="bg-ivory rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-fir text-ivory px-6 py-5 flex items-center justify-between sticky top-0 z-10">
            <div>
              <p className="font-display text-2xl leading-none">{t("Prenota il tuo soggiorno")}</p>
              <p className="text-sm text-ivory/70 mt-1">{t(apartment?.title)}{roomType ? ` · ${t(roomType.name)}` : ""}</p>
            </div>
            <button data-testid="booking-close-btn" onClick={onClose} className="text-ivory/80 hover:text-gold">
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {step === "quote" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Check-in" icon={<CalendarDays size={16} />}>
                    <input type="date" data-testid="booking-checkin" value={form.checkin} min={new Date().toISOString().slice(0,10)}
                      onChange={(e) => onCheckinChange(e.target.value, form.checkout, setForm)}
                      className="input-alpine" />
                  </Field>
                  <Field label="Check-out" icon={<CalendarDays size={16} />}>
                    <input type="date" data-testid="booking-checkout" value={form.checkout} min={nextDay(form.checkin)}
                      onChange={(e) => setForm({ ...form, checkout: e.target.value })}
                      className="input-alpine" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Adulti" icon={<Users size={16} />}>
                    <input type="number" min="1" max={roomType?.max_guests || apartment?.max_guests || 8} data-testid="booking-adults"
                      value={form.adults} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })}
                      className="input-alpine" />
                  </Field>
                  <Field label="Bambini (<14 anni)" icon={<Users size={16} />}>
                    <input type="number" min="0" max={roomType?.max_guests || apartment?.max_guests || 8} data-testid="booking-children"
                      value={form.children} onChange={(e) => setForm({ ...form, children: Number(e.target.value) })}
                      className="input-alpine" />
                  </Field>
                </div>

                {loading && <div className="flex justify-center py-4"><Loader2 className="animate-spin text-fir" /></div>}

                {quote && !loading && (
                  <div data-testid="booking-quote" className={`rounded-2xl p-4 border ${quote.available ? "border-wood/40 bg-white" : "border-red-300 bg-red-50"}`}>
                    {quote.available ? (
                      <>
                        <div className="flex justify-between text-sm text-fir-deep/70">
                          <span>{quote.num_nights} {quote.num_nights === 1 ? t("notte") : t("notti")} · {quote.adults} {t("adulti")}{quote.children ? `, ${quote.children} ${t("bambini")}` : ""}</span>
                          <span>€{quote.avg_per_person_night}/{t("persona a notte")}</span>
                        </div>
                        {quote.padded_nights > 0 && (
                          <p data-testid="booking-min-nights-note" className="text-xs text-wood mt-1.5">
                            {t("Soggiorno breve: minimo")} {quote.billed_nights} {t("notti conteggiate (copertura pulizie).")}
                          </p>
                        )}
                        {quote.pet_fee > 0 && (
                          <div className="flex justify-between text-sm text-fir-deep/70 mt-2">
                            <span>{t("Supplemento animali")}</span>
                            <span data-testid="booking-pet-fee">€{quote.pet_fee}</span>
                          </div>
                        )}
                        {quote.discount_amount > 0 && (
                          <div className="flex justify-between text-sm text-green-700 mt-2" data-testid="booking-discount">
                            <span>{quote.discount_type === "percent" ? `${t("Sconto")} (-${quote.discount_value}%)` : t("Sconto")}</span>
                            <span className="font-semibold">-€{quote.discount_amount}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-end mt-3 pt-3 border-t border-stone">
                          <span className="font-display text-lg text-fir">{t("Totale soggiorno")}</span>
                          <span data-testid="booking-total" className="font-display text-3xl text-fir">€{quote.grand_total ?? quote.total}</span>
                        </div>
                        <div className="mt-3 rounded-xl bg-fir/5 p-3 text-sm space-y-1">
                          <div className="flex justify-between text-fir-deep">
                            <span>{t("Cauzione da versare ora (20%)")}</span>
                            <span data-testid="booking-deposit" className="font-bold text-fir">€{Math.round((quote.grand_total ?? quote.total) * 0.2 * 100) / 100}</span>
                          </div>
                          <div className="flex justify-between text-fir-deep/70">
                            <span>{t("Saldo all'arrivo")}</span>
                            <span>€{Math.round(((quote.grand_total ?? quote.total) - (quote.grand_total ?? quote.total) * 0.2) * 100) / 100}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-red-600 text-sm font-medium">{t("Date non disponibili. Scegli altre date.")}</p>
                    )}
                  </div>
                )}

                {quote?.available && (
                  <>
                    <div className="flex gap-2">
                      <input placeholder={t("Codice sconto (opzionale)")} data-testid="booking-discount-code"
                        value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                        className="input-alpine flex-1" />
                      <button type="button" data-testid="apply-discount-code" onClick={applyCode}
                        className="px-4 rounded-xl border border-fir/30 text-fir text-sm hover:bg-fir/5 transition-colors">{t("Applica")}</button>
                    </div>
                    {codeMsg && <p data-testid="discount-code-msg" className={`text-xs ${appliedCode ? "text-green-700" : "text-red-600"}`}>{codeMsg}</p>}
                    <input placeholder={t("Nome e cognome")} data-testid="booking-name"
                      value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                      className="input-alpine w-full" />
                    <input type="email" placeholder={t("Email")} data-testid="booking-email"
                      value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                      className="input-alpine w-full" />
                    <input type="tel" placeholder={t("Numero di telefono")} data-testid="booking-phone"
                      value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                      className="input-alpine w-full" />

                    <label className="flex items-center gap-2 cursor-pointer text-sm text-fir-deep">
                      <input type="checkbox" data-testid="booking-pets" checked={form.pets}
                        onChange={(e) => setForm({ ...form, pets: e.target.checked })} className="h-4 w-4 accent-[#1E3F20]" />
                      <span>{t("Viaggio con animali")} <span className="text-fir-deep/50">{t("(+€60 per l'intero soggiorno)")}</span></span>
                    </label>

                    <div className="rounded-xl border border-wood/40 bg-white p-3 text-xs text-fir-deep/75 leading-relaxed">
                      <b className="text-fir">{t("Condizioni di cancellazione")}</b><br />
                      {t("La cauzione del 20% è rimborsabile se cancelli almeno 30 giorni prima dell'arrivo. In caso di cancellazione entro i 30 giorni, la cauzione non viene rimborsata. Il saldo (80%) si paga all'arrivo.")}
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer text-sm text-fir-deep">
                      <input type="checkbox" data-testid="booking-accept-terms" checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1E3F20]" />
                      <span>{t("Ho letto e accetto le condizioni di prenotazione e la politica di cancellazione.")}</span>
                    </label>

                    <button data-testid="booking-pay-stripe-btn" onClick={startStripePayment} disabled={loading || !acceptTerms}
                      className="gold-btn w-full py-4 rounded-xl text-lg flex items-center justify-center gap-2 disabled:opacity-50">
                      {loading ? <Loader2 className="animate-spin" size={20} /> : `${t("Paga la caparra con carta")} · €${Math.round((quote.grand_total ?? quote.total) * 0.2 * 100) / 100}`}
                    </button>
                    {config.paypal_enabled !== false && (
                      <>
                        <div className="flex items-center gap-3 text-xs text-fir-deep/40">
                          <span className="flex-1 h-px bg-stone" />{t("oppure")}<span className="flex-1 h-px bg-stone" />
                        </div>
                        <button data-testid="booking-pay-paypal-btn" onClick={startPayment} disabled={loading || !acceptTerms}
                          className="w-full py-3 rounded-xl border border-fir/30 text-fir font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-fir/5 transition-colors">
                          {t("Paga con PayPal")}
                        </button>
                      </>
                    )}
                    <p className="text-[11px] text-center text-fir-deep/45">{t("Pagamento sicuro. La caparra è il 20% del totale; il saldo si versa all'arrivo.")}</p>
                  </>
                )}
              </>
            )}

            {step === "pay" && order && config.paypal_enabled !== false && (apartment?.paypal_configured ?? config.paypal_configured) && (
              <div data-testid="paypal-container">
                <p className="text-center text-fir mb-4">Completa il pagamento della cauzione <b>€{order.deposit}</b> con PayPal</p>
                <PayPalScriptProvider options={{ clientId: apartment?.paypal_client_id || config.paypal_client_id, currency: order.currency }}>
                  <PayPalButtons
                    createOrder={() => Promise.resolve(order.order_id)}
                    onApprove={async (data) => { await capture(data.orderID, order.booking_id); }}
                    onError={() => toast.error("Errore durante il pagamento PayPal")}
                  />
                </PayPalScriptProvider>
              </div>
            )}

            {step === "done" && (
              <div data-testid="booking-success" className="text-center py-8">
                <CheckCircle2 className="mx-auto text-gold" size={64} />
                <h3 className="font-display text-3xl text-fir mt-4">Prenotazione confermata!</h3>
                <p className="text-fir-deep/70 mt-2">
                  Ti abbiamo inviato una email di conferma. {order?.balance_due != null && <>Cauzione versata €{order.deposit}, saldo €{order.balance_due} all'arrivo. </>}Non vediamo l'ora di accoglierti a Dimaro!
                </p>
                <button onClick={onClose} className="mt-6 gold-btn px-8 py-3 rounded-xl">Chiudi</button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, icon, children }) {
  const { t } = useI18n();
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-fir-deep/60 flex items-center gap-1.5 mb-1.5">{icon}{t(label)}</span>
      {children}
    </label>
  );
}
